import { Button, ColorPicker, Input, Modal, Select, Slider, Space, Switch } from 'antd';
import { useLayerProperitesContext } from '../../../context/LayerPropertiesContext';
import MapPortalService from '../../../services/MapPortal/mapportal.service';
import { useState, useEffect } from 'react';
import { Filter, FilterItem } from '../Tools/QueryToolModal';
import { useQuery } from '@tanstack/react-query';
import './LayerProperties.scss';

const fontFamilyOptions = [
  { label: 'Arial', value: 'Arial' },
  { label: 'Verdana', value: 'Verdana' },
  { label: 'Tahoma', value: 'Tahoma' },
  { label: 'Times New Roman', value: 'Times New Roman' },
  { label: 'Georgia', value: 'Georgia' },
  { label: 'Courier New', value: 'Courier New' },
  { label: 'Roboto', value: 'Roboto' },
  { label: 'Open Sans', value: 'Open Sans' },
  { label: 'Montserrat', value: 'Montserrat' },
];

const fontSizeOptions = Array.from({ length: 21 }, (_, i) => {
  const size = 10 + i * 2;
  return { label: `${size}px`, value: size };
});

const labelPositionOptions = [
  { label: 'Center', value: 'center' },
  { label: 'Offset Horizontal', value: 'offset_horizontal' },
];

const LabelsTab = () => {
  const {
    uniqueLayerName,
    displayLabels,
    labelField,
    useFilters,
    filter,
    selectedFilter,
    fontSize,
    fontFamily,
    labelColor,
    labelPosition,
    xAxisOffset,
    yAxisOffset,
    halo,
    haloColor,
    haloRadius,
    haloStrength,
    setState,
    layerTitle,
  } = useLayerProperitesContext();

  const { fetchFeaturesKeyAndValueMapping } = MapPortalService();
  const { data: featuresResponse } = useQuery(fetchFeaturesKeyAndValueMapping(uniqueLayerName));

  const [labeOptions, setLabelOptions] = useState<{ label: string; value: string }[]>([]);
  const [queryModalVisible, setQueryModalVisible] = useState(false);
  const [filters, setFilters] = useState<Filter[]>([{ category: '', condition: '', value: '', logicalOperator: '' }]);
  const [filterName, setFilterName] = useState('');
  const [features, setFeatures] = useState<any>({});
  const [valueOptions, setValueOptions] = useState<{ label: string; value: any }[]>([]);
  const [selectedKey, setSelectedKey] = useState<string>('');

  useEffect(() => {
    if (!featuresResponse) return;
    setFeatures(featuresResponse);
    const keys = Object.keys(featuresResponse).map((key) => ({
      label: key,
      value: key,
    }));
    setLabelOptions(keys);
  }, [featuresResponse]);

  const handleValueChange = (value: string, index: number) => {
    const updatedFilters = [...filters];
    updatedFilters[index].value = value;
    setFilters(updatedFilters);
  };
  const handleAddFilter = () => setFilters([...filters, { category: '', condition: '', value: '', logicalOperator: '' }]);
  const handleRemoveFilter = (i: number) => setFilters(filters.filter((_, index) => index !== i));
  const handleCategoryChange = (value: string, index: number) => {
    const updated = [...filters];
    updated[index].category = value;
    setFilters(updated);
    setSelectedKey(value);
    const vals = (features[value] || []).map((v: any) => ({ label: String(v), value: v }));
    setValueOptions(vals);
  };
  const handleConditionChange = (value: string, index: number) => {
    const updated = [...filters];
    updated[index].condition = value;
    setFilters(updated);
  };
  const handleFeatureChange = async (value: string, index: number) => {
    handleValueChange(value, index);
  };
  const handleLogicalOperatorChange = (value: string, index: number) => {
    const updated = [...filters];
    updated[index].logicalOperator = value;
    setFilters(updated);
  };

  const handleSaveExpression = () => {
    if (!filterName.trim()) return;
    const newFilter = {
      name: filterName.trim(),
      expression: filters,
    };
    setState((prev) => ({
      ...prev,
      filter: [...(Array.isArray(prev.filter) ? prev.filter : []), newFilter],
    }));
    setFilterName('');
    setFilters([{ category: '', condition: '', value: '', logicalOperator: '' }]);
    setQueryModalVisible(false);
  };

  return (
    <>
      <h3 className="lp-tab__title--no-margin">{layerTitle}</h3>
      <div className="lp-controls__row">
        <span className="lp-controls__label">DISPLAY LABELS</span>
        <Switch checked={displayLabels} onChange={(checked) => setState((prev) => ({ ...prev, displayLabels: checked }))} />
      </div>
      {displayLabels && (
        <>
          <div className="lp-controls__row--no-margin">
            <span className="lp-controls__label--fixed">LABEL FIELD</span>
            <Select
              className="lp-controls__flex"
              placeholder="Select One"
              value={labelField}
              onChange={(value) => setState((prev) => ({ ...prev, labelField: value }))}
              options={labeOptions}
              allowClear
            />
          </div>
          <div className="lp-controls__row--mt">
            <span className="lp-controls__label">USE FILTERS</span>
            <Switch checked={useFilters} onChange={(checked) => setState((prev) => ({ ...prev, useFilters: checked }))} />
          </div>
          {useFilters && (
            <>
              <div className="lp-controls__row--mt">
                <span className="lp-controls__label--fixed">FILTER</span>
                <Select
                  className="lp-controls__flex"
                  placeholder="Select saved filter"
                  value={selectedFilter}
                  onChange={(value) =>
                    setState((prev) => ({
                      ...prev,
                      selectedFilter: value,
                    }))
                  }
                  options={filter?.map((f) => ({ label: f.name, value: f.name })) ?? []}
                />
                <Button type="dashed" onClick={() => setQueryModalVisible(true)} className="lp-controls__btn-ml">
                  Create Expression
                </Button>
              </div>
            </>
          )}
          <div className="lp-controls__row--mt">
            <span className="lp-controls__label--fixed">FONT</span>
            <Space>
              <Select
                className="lp-controls__fixed"
                placeholder="Font Family"
                value={fontFamily}
                onChange={(value) => setState((prev) => ({ ...prev, fontFamily: value }))}
                options={fontFamilyOptions}
              />
              <Select
                className="lp-controls__fixed"
                placeholder="Font Size"
                value={fontSize ? `${fontSize}px` : undefined}
                onChange={(value) => setState((prev) => ({ ...prev, fontSize: value }))}
                options={fontSizeOptions}
              />
            </Space>
          </div>
          <div className="lp-controls__row--mt">
            <span className="lp-controls__label--fixed">LABEL COLOUR</span>
            <ColorPicker
              className="custom-button"
              value={labelColor}
              onChange={(value) =>
                setState((prev) => ({
                  ...prev,
                  labelColor: value.toHexString(),
                }))
              }
              showText
            />
          </div>
          <div className="lp-controls__row--mt">
            <span className="lp-controls__label--fixed">LABEL POSITION</span>
            <Select
              className="lp-controls__flex"
              placeholder="Select One"
              value={labelPosition}
              onChange={(value) => setState((prev) => ({ ...prev, labelPosition: value }))}
              options={labelPositionOptions}
            />
          </div>
          <div className="lp-controls__row--mt">
            <span className="lp-controls__label--fixed">X AXIS OFFSET</span>
            <Slider
              className="lp-controls__flex"
              value={xAxisOffset}
              onChange={(value) => setState((prev) => ({ ...prev, xAxisOffset: value || 0 }))}
            />
          </div>
          <div className="lp-controls__row--mt">
            <span className="lp-controls__label--fixed">Y AXIS OFFSET</span>
            <Slider className="lp-controls__flex" value={yAxisOffset} onChange={(value) => setState((prev) => ({ ...prev, yAxisOffset: value }))} />
          </div>
          <div className="lp-controls__row--mt">
            <span className="lp-controls__label">HALO</span>
            <Switch checked={halo} onChange={(checked) => setState((prev) => ({ ...prev, halo: checked }))} />
          </div>
          <div className="lp-controls__row--no-margin">
            <span className="lp-controls__label--fixed">HALO COLOUR</span>
            <ColorPicker
              className="custom-button"
              value={haloColor}
              onChange={(value) =>
                setState((prev) => ({
                  ...prev,
                  haloColor: value.toHexString(),
                }))
              }
              showText
            />
          </div>
          <div className="lp-controls__row--mt">
            <span className="lp-controls__label--fixed">HALO STRENGTH</span>
            <Slider className="lp-controls__flex" value={haloStrength} onChange={(value) => setState((prev) => ({ ...prev, haloStrength: value }))} />
          </div>
          <div className="lp-controls__row--mt">
            <span className="lp-controls__label--fixed">HALO RADIUS</span>
            <Slider className="lp-controls__flex" value={haloRadius} onChange={(value) => setState((prev) => ({ ...prev, haloRadius: value }))} />
          </div>
        </>
      )}
      <Modal
        title="Create Filter Expression"
        open={queryModalVisible}
        onCancel={() => setQueryModalVisible(false)}
        onOk={handleSaveExpression}
        okButtonProps={{ disabled: !filterName.trim() }}
        width={700}
      >
        <Input placeholder="Enter filter name" value={filterName} onChange={(e) => setFilterName(e.target.value)} className="lp-controls__mb" />

        {filters.map((filter, index) => (
          <FilterItem
            key={index}
            index={index}
            filter={filter}
            keyOptions={labeOptions}
            valueOptions={valueOptions}
            selectedKey={selectedKey}
            layerName={uniqueLayerName}
            handleCategoryChange={handleCategoryChange}
            handleConditionChange={handleConditionChange}
            handleLogicalOperatorChange={handleLogicalOperatorChange}
            handleFeatureChange={handleFeatureChange}
            handleValueChange={handleValueChange}
            handleRemoveFilter={handleRemoveFilter}
            setDrawerItems={() => {}}
          />
        ))}

        <Button type="dashed" onClick={handleAddFilter}>
          Add Filter
        </Button>
      </Modal>
    </>
  );
};

export default LabelsTab;
